import { BarChartIcon, FolderIcon, ReceiptIcon, SparklesIcon } from '@/components/ui/icons';
import { FEATURE_CATEGORIES, FeatureCategoryId } from '@/providers/features-category.provider';
import gsap from 'gsap';
import { ReactNode, useEffect, useRef } from 'react';

interface FeaturesDropdownProps {
  isOpen: boolean;
  onSelect: (id: FeatureCategoryId) => void;
}

const CATEGORY_META: Record<FeatureCategoryId, ReactNode> = {
  facturation: <ReceiptIcon />,
  gestion: <BarChartIcon />,
  documents: <FolderIcon />,
  innovation: <SparklesIcon />,
};

export function FeaturesDropdown({ isOpen, onSelect }: FeaturesDropdownProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!panelRef.current) return;

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (isOpen) {
      gsap.fromTo(
        panelRef.current,
        { autoAlpha: 0, y: -6, scale: 0.98 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 0.2,
          ease: 'power2.out',
          transformOrigin: 'top center',
        },
      );
      const validItems = itemsRef.current.filter(Boolean) as HTMLButtonElement[];
      if (validItems.length > 0) {
        gsap.fromTo(
          validItems,
          { autoAlpha: 0, y: 3 },
          { autoAlpha: 1, y: 0, duration: 0.15, stagger: 0.035, ease: 'power2.out', delay: 0.05 },
        );
      }
    } else {
      gsap.to(panelRef.current, {
        autoAlpha: 0,
        y: -6,
        scale: 0.98,
        duration: 0.15,
        ease: 'power2.in',
      });
    }
  }, [isOpen]);

  return (
    // Transparent wrapper that covers the gap between the nav button and the panel.
    // pointer-events:none when closed so it doesn't intercept hover events.
    <div
      className="absolute top-full left-0 z-10 pt-2"
      style={{ pointerEvents: isOpen ? 'auto' : 'none' }}
    >
      <div
        ref={panelRef}
        className="border-dark-blue/8 w-56 overflow-hidden rounded-xl border bg-white shadow-[0_8px_28px_rgba(57,68,84,0.10)] backdrop-blur-lg"
        style={{ visibility: 'hidden', opacity: 0 }}
      >
        <div className="p-1.5">
          {FEATURE_CATEGORIES.map((category, i) => (
            <button
              key={category.id}
              ref={(el) => {
                itemsRef.current[i] = el;
              }}
              className="group hover:bg-orange/5 flex w-full cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors duration-150"
              onClick={() => onSelect(category.id)}
            >
              <span className="bg-dark-blue/5 text-dark-blue/50 group-hover:bg-orange/10 group-hover:text-orange flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors duration-150">
                {CATEGORY_META[category.id]}
              </span>
              <span className="text-dark-blue/80 group-hover:text-orange text-sm transition-colors duration-150">
                {category.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
